import { queryOptions, useQuery } from "@tanstack/react-query";

import { apiClient } from "./apiClient";
import { ApiRequestError, isApiRequestError } from "./errors";
import { queryKeys } from "./queryKeys";

// Shared `GET /v1/system/license` read used by both the app-wide banner
// (AppLayout) and the /settings/license page. Warn-only license signage must
// surface a newly reached warning / grace / locked / 95% state without a page
// reload, but the shared QueryClient disables focus refetch
// (lib/queryClient.ts) and the SPA has no license mutation to invalidate on.
// So the status refetches on a slow interval while any observer is mounted.
// Five minutes is ample: this signage is never realtime and never blocks work
// (docs/08-on-prem-readiness.md, "XIV — Offline signed licensing":
// "Quantity enforcement is warn + report, never block").
export const LICENSE_REFETCH_INTERVAL_MS = 5 * 60_000;

const LICENSE_TRANSIENT_RETRIES = 2;

// Retry only server-declared transient failures; a terminal 4xx (e.g. auth)
// must not spin.
function isTransient(error: unknown): boolean {
  return isApiRequestError(error) && error.status.retryable;
}

export function licenseStatusQueryOptions() {
  return queryOptions({
    queryKey: queryKeys.license,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/system/license");
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    retry: (failureCount, error) =>
      failureCount < LICENSE_TRANSIENT_RETRIES && isTransient(error),
    refetchInterval: LICENSE_REFETCH_INTERVAL_MS,
  });
}

/**
 * Is this install licensed at all?
 *
 * The install-profile seam for the licensing surface: hosted installs carry no
 * license and answer `not_required`, so the License row and its palette entry
 * are hidden there rather than pointing at a permanently empty page. Read from
 * the licensing API itself — never inferred from the hosted billing flag,
 * which answers a different question (is Stripe wired) and would couple two
 * unrelated capabilities.
 *
 * FALSE while the status is still loading or failed to load: a gated row must
 * appear once its capability is known, never flash and vanish. This is the
 * same cached query the app-wide banner already holds, so it costs no request.
 */
export function useIsLicensed(): boolean {
  const license = useQuery(licenseStatusQueryOptions());
  return license.data != null && license.data.state !== "not_required";
}
